import { toBlob } from 'html-to-image';
import { OUTCOMES } from '../config.js';

function canWriteImageToClipboard() {
    return (
        typeof navigator !== 'undefined' &&
        typeof navigator.clipboard?.write === 'function' &&
        typeof window !== 'undefined' &&
        typeof window.ClipboardItem === 'function'
    );
}

/**
 * @param {import('../systems/gameState.js').ReturnType<typeof import('../systems/gameState.js').createInitialState>} state
 * @param {(key: string, fallback?: string) => string} t
 */
export function resolveResultTitleKey(state) {
    if (state.outcome === OUTCOMES.DIED) return 'result.died';
    if (state.outcome === OUTCOMES.SKIPPED) return 'result.skipped';
    return 'result.finished';
}

/**
 * Plain-text fallback for chat apps when image clipboard is unavailable.
 * @param {import('../systems/gameState.js').ReturnType<typeof import('../systems/gameState.js').createInitialState>} state
 * @param {(key: string, fallback?: string) => string} t
 */
export function buildResultShareText(state, t) {
    const titleKey = resolveResultTitleKey(state);
    const goodTotal = state.stats.goodHits + state.stats.hotHits + (state.stats.activityHits || 0);

    return [
        t('game.title', 'Lead Pop'),
        t(titleKey),
        `${t('result.score')}: ${state.score}`,
        `${t('result.goodHits')}: ${goodTotal}`,
        `${t('result.badHits')}: ${state.stats.badHits}`,
    ].join('\n');
}

/**
 * @param {HTMLElement} element
 * @returns {Promise<Blob>}
 */
export async function captureResultCardBlob(element) {
    if (!element) {
        throw new Error('Result card element is required');
    }

    const blob = await toBlob(element, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
    });

    if (!blob) {
        throw new Error('Could not render result card image');
    }

    return blob;
}

/**
 * @param {Blob} blob
 */
export async function writeImageBlobToClipboard(blob) {
    if (!canWriteImageToClipboard()) {
        throw new Error('Image clipboard is not supported');
    }

    await navigator.clipboard.write([
        new ClipboardItem({
            'image/png': blob,
        }),
    ]);
}

/**
 * @param {string} text
 */
export async function writeTextToClipboard(text) {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        throw new Error('Text clipboard is not supported');
    }

    await navigator.clipboard.writeText(text);
}

/**
 * Copy the result card for chat paste (image first, text fallback).
 * @param {HTMLElement} element
 * @param {import('../systems/gameState.js').ReturnType<typeof import('../systems/gameState.js').createInitialState>} state
 * @param {(key: string, fallback?: string) => string} t
 * @returns {Promise<'image' | 'text'>}
 */
export async function copyResultCard(element, state, t) {
    try {
        const blob = await captureResultCardBlob(element);
        await writeImageBlobToClipboard(blob);
        return 'image';
    } catch {
        const text = buildResultShareText(state, t);
        await writeTextToClipboard(text);
        return 'text';
    }
}
